import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
  SessionDetail,
  SessionStatusEnum,
  SessionTypeEnum,
} from 'src/app/@data-services/event-agenda/event-agenda.data-model';
import { EventAgendaDataService } from 'src/app/@data-services/event-agenda/event-agenda.data-service';
import { SessionInsightsDataService } from 'src/app/@data-services/session-insights/session-insights-agenda.data-service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';

const isLiveOrUpcoming = (status: string | undefined | null): boolean =>
  status === SessionStatusEnum.InProgress ||
  status === SessionStatusEnum.NotStarted;

/**
 * SYN-2365: Inverse of determineSessionStatusResolver. Guards the Debrief
 * routes so that an attendee who lands on /session/:id/debrief (refresh or
 * direct URL) while the session has been restarted to live is sent to the
 * live insights view instead of being shown the stale Debrief page.
 */
export const determineDebriefStatusResolver: ResolveFn<boolean> = (
  route,
  _state
) => {
  if (route.queryParamMap.get('internal')) {
    return true;
  }

  const router = inject(Router);
  const eventAgendaDataService = inject(EventAgendaDataService);
  const sessionInsightsDataService = inject(SessionInsightsDataService);
  const eventConfigStateService = inject(EventConfigStateService);

  // Mirror determineSessionStatusResolver: hydrate the primary-screen flag
  // from the URL so direct/refresh loads of /debrief?isPrimaryScreen=... land
  // with the store already in the correct mode, and so an explicit
  // ?isPrimaryScreen=false also clears the flag (the component's own ngOnInit
  // only handles the truthy case).
  const isPrimaryScreenParam = route.queryParamMap.get('isPrimaryScreen');
  if (isPrimaryScreenParam !== null) {
    eventConfigStateService.setPrimaryScreen(isPrimaryScreenParam === 'true');
  }

  const event = eventConfigStateService.$eventIdentifier();
  const sessionId = route.paramMap.get('sessionId') ?? '';

  const decide = (
    status: string | undefined | null
  ): boolean | RedirectCommand =>
    isLiveOrUpcoming(status)
      ? new RedirectCommand(
          // Carry `internal` so the live resolver's escape hatch fires and it
          // can't bounce us back to debrief — the matching guard to the one in
          // determine-session-status.resolver. `merge` preserves existing
          // params (isPrimaryScreen / language) while adding internal.
          router.createUrlTree(['session', sessionId], {
            queryParams: { internal: '1' },
            queryParamsHandling: 'merge',
          }),
          { skipLocationChange: true }
        )
      : true;

  // Full-agenda check, kept for the cases one session cannot answer.
  const checkAgenda = (): Observable<boolean | RedirectCommand> =>
    eventAgendaDataService
      .getEventDetails({ event })
      .pipe(
        map((response) =>
          decide(
            response.data.find((e) => e.PrimarySessionId === sessionId)?.Status
          )
        )
      );

  // The agenda only differs from the one-session answer for a primary
  // session (a live breakout marks it live) or a row the agenda would skip.
  const answerFromSession = (
    session: SessionDetail | null | undefined
  ): Observable<boolean | RedirectCommand> => {
    if (
      !session ||
      session.PrimarySessionId !== sessionId ||
      session.Type === SessionTypeEnum.BreakoutSession
    ) {
      return checkAgenda();
    }
    if (isLiveOrUpcoming(session.Status)) {
      return of(decide(session.Status));
    }
    return session.Type === SessionTypeEnum.PrimarySession
      ? checkAgenda()
      : of(true);
  };

  // Fast path: fetch only this session (~0.5 KB instead of the ~1.4 MB agenda).
  return sessionInsightsDataService
    .getSessionDetails({ event, sessionId })
    .pipe(
      catchError(() => of(null)),
      switchMap((response) => answerFromSession(response?.data))
    );
};
